/**
 * AI Dosimetry Workflow Service.
 *
 * Provides domain-specific access to loaded visualization data for AI Dosimetry cases:
 * - CT Portal volume for 2D rendering (orthogonal slices)
 * - CT Arterial volume for 3D volume rendering
 * - Couinaud segment label map + individual meshes
 * - Tumor label map + individual meshes
 * - Structures label map (Parenchyma + Tumors aggregate, for volume computation only)
 *
 * Computes segment and tumor volumes from label maps,
 * initializes the DosimetryStateService with computed volumes.
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import { DosimetryStateService } from './dosimetry-state-service';
import { TUMOR_COLOR, getSegmentColor } from '../domain/segment-colors';
import { computeAllLabelVolumes, computeTumorSegmentOverlaps } from '../domain/volume-computation';
import { DOSIMETRY_FEATURE_ID } from '../dosimetry-constants';

import type { ServiceContext } from '@/visualization/core/types';
import { DisplayFeature } from '@/visualization/display/display-feature';
import type { StructureSetFeatureData } from '@/visualization/display/objects/structure-set-types';
import { DisplayFeatureType } from '@/visualization/display/types';
import type { TepOverlayDisplayData } from '@/visualization/display/types';
import { LabelMap } from '@/visualization/domain/label-map';
import type { RGB } from '@/visualization/domain/types';
import type { ILabelMapStructure } from '@/visualization/domain/types';
import { Volume } from '@/visualization/domain/volume';
import { CurrentLocation, CurrentLocationService } from '@/visualization/services';
import { ViewportDataService } from '@/visualization/services/viewport-data-service';
import { parseMesh, resampleVolumeToSpacing } from '@/visualization/utils';
import type { TransformErrorPayload } from '@/visualization/workflows/base-workflow-service';
import { BaseWorkflowService } from '@/visualization/workflows/base-workflow-service';

// ============================================================================
// Slot Keys
// ============================================================================

const DOSI_SLOTS = {
    ctPortal: 'ct-portal',
    ctArterial: 'ct-arterial',
    segCouinaud: 'seg-couinaud',
    segTumors: 'seg-tumors',
    segStructures: 'seg-structures',
} as const;

// ============================================================================
// Event Types
// ============================================================================

export interface DosimetryDataReadyPayload {
    ctPortal: boolean;
    ctArterial: boolean;
    segCouinaud: boolean;
    segTumors: boolean;
    segStructures: boolean;
}

// ============================================================================
// Service
// ============================================================================

export class DosimetryWorkflowService extends BaseWorkflowService {
    static readonly SERVICE_NAME = 'DosimetryWorkflowService';
    readonly WORKFLOW_ID = 'ai-dosimetry';

    override readonly EVENTS = {
        LOAD_PROGRESS: 'workflow:load:progress',
        LOAD_COMPLETE: 'workflow:load:complete',
        TRANSFORM_ERROR: 'workflow:transform:error',
        CASE_METADATA_SET: 'workflow:case-metadata:set',
        DATA_READY: 'dosimetry:data:ready',
    } as const;

    // Domain objects
    private workflowUnsubscribers: Array<() => void> = [];
    private meshLoadAbortController: AbortController | null = null;
    private ctPortalVolume: Volume | null = null;
    private ctArterialVolume: Volume | null = null;
    private maskedCTVolume: Volume | null = null;
    private segCouinaud: LabelMap | null = null;
    private segTumors: LabelMap | null = null;
    private segStructures: LabelMap | null = null;
    private segmentVolumes: Map<number, number> = new Map();
    private tumorVolumes: Map<number, number> = new Map();

    /** Overlap volumes: tumorLabel → segmentLabel → overlap mL */
    private tumorSegmentOverlaps: ReadonlyMap<number, ReadonlyMap<number, number>> = new Map();

    // Structure set feature data (mutable references for async mesh loading)
    private segmentMeshes = new Map<number, unknown>();
    private tumorMeshes = new Map<number, unknown>();

    constructor(context: ServiceContext) {
        super(context);
    }

    // ========================================================================
    // Template Method Implementations
    // ========================================================================

    protected getDataSlots(): string[] {
        return [
            DOSI_SLOTS.ctPortal,
            DOSI_SLOTS.ctArterial,
            DOSI_SLOTS.segCouinaud,
            DOSI_SLOTS.segTumors,
            DOSI_SLOTS.segStructures,
        ];
    }

    protected transformLoadedData(): void {
        this.transformCTPortal();
        this.transformCTArterial();
        this.transformSegCouinaud();
        this.transformSegTumors();
        this.transformSegStructures();

        this.computeVolumes();
        this.createMaskedCTVolume();
        this.populateDataSlots();
        this.initializeDosimetryState();

        this.broadcast(this.EVENTS.DATA_READY, {
            ctPortal: this.ctPortalVolume !== null,
            ctArterial: this.ctArterialVolume !== null,
            segCouinaud: this.segCouinaud !== null,
            segTumors: this.segTumors !== null,
            segStructures: this.segStructures !== null,
        } satisfies DosimetryDataReadyPayload);
    }

    protected createDisplayFeatures(): void {
        this.createSegmentStructureSet();
        this.createTumorStructureSet();
        this.createCurrentLocationFeature();
        this.subscribeToHighlightChanges();
        this.subscribeToTepVolumeChanges();

        // Load mesh polydata asynchronously — features render once data arrives
        this.loadAllMeshData();
    }

    isReady(): boolean {
        return this.ctPortalVolume !== null;
    }

    // ========================================================================
    // Domain Transformation
    // ========================================================================

    private transformCTPortal(): void {
        const imageData = this.dataLoader.getVolumeData(DOSI_SLOTS.ctPortal);
        if (!imageData) {
            console.warn(`[DosimetryWorkflow] No volume data for slot "${DOSI_SLOTS.ctPortal}"`);
            return;
        }
        this.ctPortalVolume = new Volume(DOSI_SLOTS.ctPortal, imageData);
    }

    private transformCTArterial(): void {
        const imageData = this.dataLoader.getVolumeData(DOSI_SLOTS.ctArterial);
        if (!imageData) {
            console.warn(`[DosimetryWorkflow] No volume data for slot "${DOSI_SLOTS.ctArterial}" (optional)`);
            return;
        }
        this.ctArterialVolume = new Volume(DOSI_SLOTS.ctArterial, imageData);
    }

    private transformSegCouinaud(): void {
        const imageData = this.dataLoader.getVolumeData(DOSI_SLOTS.segCouinaud);
        if (!imageData) {
            console.warn(`[DosimetryWorkflow] No volume data for slot "${DOSI_SLOTS.segCouinaud}"`);
            return;
        }
        const metadata = this.dataLoader.getSlotMetadata(DOSI_SLOTS.segCouinaud);
        const structures = this.extractStructures(metadata);
        this.segCouinaud = new LabelMap(DOSI_SLOTS.segCouinaud, imageData, structures);
    }

    private transformSegTumors(): void {
        const imageData = this.dataLoader.getVolumeData(DOSI_SLOTS.segTumors);
        if (!imageData) {
            console.warn(`[DosimetryWorkflow] No volume data for slot "${DOSI_SLOTS.segTumors}"`);
            return;
        }
        const metadata = this.dataLoader.getSlotMetadata(DOSI_SLOTS.segTumors);
        const structures = this.extractStructures(metadata);
        this.segTumors = new LabelMap(DOSI_SLOTS.segTumors, imageData, structures);
    }

    private transformSegStructures(): void {
        const imageData = this.dataLoader.getVolumeData(DOSI_SLOTS.segStructures);
        if (!imageData) {
            console.warn(`[DosimetryWorkflow] No volume data for slot "${DOSI_SLOTS.segStructures}"`);
            return;
        }
        const metadata = this.dataLoader.getSlotMetadata(DOSI_SLOTS.segStructures);
        const structures = this.extractStructures(metadata);
        this.segStructures = new LabelMap(DOSI_SLOTS.segStructures, imageData, structures);
    }

    // ========================================================================
    // Volume Computation
    // ========================================================================

    private computeVolumes(): void {
        if (this.segCouinaud) {
            const labelValues = this.segCouinaud.getLabelValues();
            this.segmentVolumes = new Map(computeAllLabelVolumes(this.segCouinaud.imageData, labelValues));
        }

        if (this.segTumors) {
            const labelValues = this.segTumors.getLabelValues();
            this.tumorVolumes = new Map(computeAllLabelVolumes(this.segTumors.imageData, labelValues));
        }

        if (this.segCouinaud && this.segTumors) {
            this.tumorSegmentOverlaps = computeTumorSegmentOverlaps(
                this.segCouinaud.imageData,
                this.segTumors.imageData,
                this.segCouinaud.getLabelValues(),
                this.segTumors.getLabelValues()
            );
        }
    }

    // ========================================================================
    // 3D Volume Rendering
    // ========================================================================

    private createMaskedCTVolume(): void {
        // Use arterial CT for 3D volume rendering if available, otherwise portal
        const sourceVolume = this.ctArterialVolume ?? this.ctPortalVolume;
        if (!sourceVolume) return;

        const resampledCT = resampleVolumeToSpacing(sourceVolume.imageData, [1.5, 1.5, 1.5]);

        this.maskedCTVolume = new Volume('masked-ct-volume', resampledCT);
    }

    // ========================================================================
    // Viewport Data Slots
    // ========================================================================

    private populateDataSlots(): void {
        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );

        if (this.ctArterialVolume || this.ctPortalVolume) {
            dataService.setSlot('ct-volume', this.ctArterialVolume ?? this.ctPortalVolume!);
        }

        if (this.maskedCTVolume) {
            dataService.setSlot('rendering-volume', this.maskedCTVolume);
        }
    }

    // ========================================================================
    // Initialize Dosimetry State
    // ========================================================================

    private initializeDosimetryState(): void {
        const stateService = this.context.services.getService<DosimetryStateService>(
            DosimetryStateService.SERVICE_NAME
        );

        stateService.initialize(this.segmentVolumes, this.tumorVolumes, this.tumorSegmentOverlaps);
    }

    // ========================================================================
    // Display Features — Structure Sets
    // ========================================================================

    private segmentFeatureId(): string {
        return DOSIMETRY_FEATURE_ID.SEGMENTS;
    }

    private tumorFeatureId(): string {
        return DOSIMETRY_FEATURE_ID.TUMORS;
    }

    private createSegmentStructureSet(): void {
        if (!this.segCouinaud) return;

        const colors = new Map<number, RGB>();
        for (const structure of this.segCouinaud.structures) {
            colors.set(structure.labelValue, getSegmentColor(structure.labelValue));
        }

        this.segmentMeshes = new Map();

        this.displayManager.addFeature(
            new DisplayFeature<DisplayFeatureType.STRUCTURE_SET, StructureSetFeatureData>({
                id: this.segmentFeatureId(),
                type: DisplayFeatureType.STRUCTURE_SET,
                data: { labelMap: this.segCouinaud, meshes: this.segmentMeshes },
                properties: {
                    colors,
                    kind: 'segment',
                    hoveredKey: null,
                    selectedKeys: new Set(),
                },
            })
        );
    }

    private createTumorStructureSet(): void {
        if (!this.segTumors) return;

        const colors = new Map<number, RGB>();
        for (const structure of this.segTumors.structures) {
            colors.set(structure.labelValue, TUMOR_COLOR);
        }

        this.tumorMeshes = new Map();

        this.displayManager.addFeature(
            new DisplayFeature<DisplayFeatureType.STRUCTURE_SET, StructureSetFeatureData>({
                id: this.tumorFeatureId(),
                type: DisplayFeatureType.STRUCTURE_SET,
                data: { labelMap: this.segTumors, meshes: this.tumorMeshes },
                properties: {
                    colors,
                    kind: 'tumor',
                    hoveredKey: null,
                    selectedKeys: new Set(),
                },
            })
        );
    }

    // ========================================================================
    // Display Features — Current Location
    // ========================================================================

    private createCurrentLocationFeature(): void {
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        const currentLocation = locationService.getLocation();

        this.displayManager.addFeature(
            new DisplayFeature({
                id: DOSIMETRY_FEATURE_ID.CURRENT_LOCATION,
                type: DisplayFeatureType.CURRENT_LOCATION,
                data: currentLocation,
                properties: {
                    opacity: 1,
                    sphere: { radius: 2.5, color: [1, 0, 0] },
                    crosshair: { color: '#00FF00' },
                },
            })
        );

        this.workflowUnsubscribers.push(
            locationService.subscribe<CurrentLocation>(
                locationService.EVENTS.LOCATION_CHANGED,
                (location) => {
                    this.displayManager.updateData<DisplayFeatureType.CURRENT_LOCATION>(
                        DOSIMETRY_FEATURE_ID.CURRENT_LOCATION,
                        location
                    );
                }
            )
        );
    }

    // ========================================================================
    // Highlight Subscription
    // ========================================================================

    private subscribeToHighlightChanges(): void {
        const stateService = this.context.services.getService<DosimetryStateService>(
            DosimetryStateService.SERVICE_NAME
        );

        this.workflowUnsubscribers.push(
            stateService.subscribe(stateService.EVENTS.HIGHLIGHT_CHANGED, () =>
                this.onHighlightChanged(stateService)
            )
        );
    }

    private onHighlightChanged(stateService: DosimetryStateService): void {
        const hoveredKey = stateService.getHoveredStructure();
        const selectedKeys = stateService.getSelectedStructures();

        this.displayManager.updateFeature<DisplayFeatureType.STRUCTURE_SET>(this.segmentFeatureId(), {
            hoveredKey,
            selectedKeys,
        });

        this.displayManager.updateFeature<DisplayFeatureType.STRUCTURE_SET>(this.tumorFeatureId(), {
            hoveredKey,
            selectedKeys,
        });
    }

    // ========================================================================
    // TEP Overlay Feature
    // ========================================================================

    private tepOverlayFeatureId(): string {
        return DOSIMETRY_FEATURE_ID.TEP_OVERLAY;
    }

    private subscribeToTepVolumeChanges(): void {
        const stateService = this.context.services.getService<DosimetryStateService>(
            DosimetryStateService.SERVICE_NAME
        );

        this.workflowUnsubscribers.push(
            stateService.subscribe(stateService.EVENTS.TEP_VOLUME_LOADED, () =>
                this.updateTepOverlayFeature(stateService)
            ),
            stateService.subscribe(stateService.EVENTS.TEP_VOLUME_REMOVED, () =>
                this.updateTepOverlayFeature(stateService)
            ),
            stateService.subscribe(stateService.EVENTS.SELECTED_PV_CHANGED, () =>
                this.updateTepOverlayFeature(stateService)
            )
        );
    }

    private updateTepOverlayFeature(stateService: DosimetryStateService): void {
        const tepVolume = stateService.getSelectedTepVolume();
        const featureId = this.tepOverlayFeatureId();

        if (!tepVolume) {
            // No TEP for the selected PV — remove the feature if it exists
            if (this.displayManager.hasFeature(featureId)) {
                this.displayManager.removeFeature(featureId);
            }
            return;
        }

        if (this.displayManager.hasFeature(featureId)) {
            // Feature exists — update its data (new PV selected with a different TEP)
            this.displayManager.updateData<DisplayFeatureType.TEP_OVERLAY>(featureId, {
                tepVolume,
            });
        } else {
            // Create the TEP overlay feature (initially hidden — visibility hook decides)
            this.displayManager.addFeature(
                new DisplayFeature<DisplayFeatureType.TEP_OVERLAY, TepOverlayDisplayData>({
                    id: featureId,
                    type: DisplayFeatureType.TEP_OVERLAY,
                    data: { tepVolume },
                    properties: { opacity: 0.7 },
                    visible: false,
                })
            );
        }
    }

    // ========================================================================
    // Mesh Loading
    // ========================================================================

    /**
     * Load all mesh polydata asynchronously.
     * Fetches each mesh file referenced by label map structures,
     * parses it into vtkPolyData, and updates the structure set features.
     * Uses an AbortController so in-flight requests are cancelled on reset.
     */
    private loadAllMeshData(): void {
        const caseId = this.dataLoader.getCurrentCaseId();
        if (!caseId) return;

        this.meshLoadAbortController?.abort();
        this.meshLoadAbortController = new AbortController();
        const { signal } = this.meshLoadAbortController;

        const entries = this.collectMeshEntries();

        for (const entry of entries) {
            this.loadSingleMesh(caseId, entry, signal);
        }
    }

    private collectMeshEntries(): Array<{
        featureId: string;
        meshKey: string;
        labelValue: number;
        meshesMap: Map<number, unknown>;
    }> {
        const entries: Array<{
            featureId: string;
            meshKey: string;
            labelValue: number;
            meshesMap: Map<number, unknown>;
        }> = [];

        if (this.segCouinaud) {
            for (const structure of this.segCouinaud.structures) {
                if (structure.mesh) {
                    entries.push({
                        featureId: this.segmentFeatureId(),
                        meshKey: structure.mesh,
                        labelValue: structure.labelValue,
                        meshesMap: this.segmentMeshes,
                    });
                }
            }
        }

        if (this.segTumors) {
            for (const structure of this.segTumors.structures) {
                if (structure.mesh) {
                    entries.push({
                        featureId: this.tumorFeatureId(),
                        meshKey: structure.mesh,
                        labelValue: structure.labelValue,
                        meshesMap: this.tumorMeshes,
                    });
                }
            }
        }

        return entries;
    }

    private async loadSingleMesh(
        caseId: string,
        entry: { featureId: string; meshKey: string; labelValue: number; meshesMap: Map<number, unknown> },
        signal: AbortSignal
    ): Promise<void> {
        try {
            const buffer = await this.dataLoader.loadDataByKey(caseId, entry.meshKey, signal);
            if (!buffer) {
                console.warn(`[DosimetryWorkflow] Mesh not found: ${entry.meshKey}`);
                return;
            }
            if (signal.aborted) return;

            const polyData = parseMesh(buffer);

            // Add to the shared meshes map and notify the display feature
            entry.meshesMap.set(entry.labelValue, polyData);

            const feature = this.displayManager.getFeature(entry.featureId);
            if (feature) {
                // Trigger FEATURE_UPDATED so display objects pick up the new mesh
                this.displayManager.updateData<DisplayFeatureType.STRUCTURE_SET>(
                    entry.featureId,
                    feature.data
                );
            }
        } catch (error) {
            if (signal.aborted) return;
            console.error(`[DosimetryWorkflow] Failed to load mesh "${entry.meshKey}":`, error);
            this.broadcast(this.EVENTS.TRANSFORM_ERROR, {
                message: `3D mesh for structure "${entry.meshKey}" could not be loaded. Contour display may be incomplete.`,
                error,
            } satisfies TransformErrorPayload);
        }
    }

    // ========================================================================
    // Domain Object Accessors
    // ========================================================================

    getCTPortalVolume(): Volume | undefined {
        return this.ctPortalVolume ?? undefined;
    }

    getCTPortalImageData(): vtkImageData | undefined {
        return this.ctPortalVolume?.imageData;
    }

    getCTArterialVolume(): Volume | undefined {
        return this.ctArterialVolume ?? undefined;
    }

    getMaskedCTVolume(): Volume | undefined {
        return this.maskedCTVolume ?? undefined;
    }

    getCouinaudLabelMap(): LabelMap | undefined {
        return this.segCouinaud ?? undefined;
    }

    getTumorsLabelMap(): LabelMap | undefined {
        return this.segTumors ?? undefined;
    }

    getStructuresLabelMap(): LabelMap | undefined {
        return this.segStructures ?? undefined;
    }

    getSegmentVolumes(): ReadonlyMap<number, number> {
        return this.segmentVolumes;
    }

    getTumorVolumes(): ReadonlyMap<number, number> {
        return this.tumorVolumes;
    }

    // ========================================================================
    // Helpers
    // ========================================================================

    private extractStructures(metadata: Record<string, unknown> | undefined): ILabelMapStructure[] {
        if (metadata && Array.isArray(metadata.structures)) {
            return metadata.structures as ILabelMapStructure[];
        }
        console.warn('No structures found in label map metadata');
        return [];
    }

    // ========================================================================
    // Reset
    // ========================================================================

    override reset(): void {
        this.meshLoadAbortController?.abort();
        this.meshLoadAbortController = null;

        this.workflowUnsubscribers.forEach((unsub) => unsub());
        this.workflowUnsubscribers = [];

        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );
        dataService.clearAll();

        const stateService = this.context.services.getService<DosimetryStateService>(
            DosimetryStateService.SERVICE_NAME
        );
        stateService.reset();

        this.ctPortalVolume = null;
        this.ctArterialVolume = null;
        this.maskedCTVolume = null;
        this.segCouinaud = null;
        this.segTumors = null;
        this.segStructures = null;
        this.segmentVolumes.clear();
        this.tumorVolumes.clear();
        this.tumorSegmentOverlaps = new Map();
        this.segmentMeshes.clear();
        this.tumorMeshes.clear();

        super.reset();
    }
}
